/**
 * Where the floating accessibility trigger sits.
 *
 * Its home is the bottom-left corner (bottom-20 left-5 — clear of the in-app
 * MobileNav and of the bottom-right SetupChecklist / toasts). On a 375×667
 * phone that corner lands on the landing hero's full-width "Get started free"
 * (CTA x=16 y=561 343×56, trigger x=20 y=539 48×48), and with the secondary
 * CTA directly beneath it no bottom spot is clear. So, like the cookie banner
 * (#965), the trigger keeps clear of any element marked `data-cookie-avoid`:
 * while its corner would cover one, it moves to the right edge, mid-screen.
 *
 * A form a floating button must never sit on is marked `data-float-avoid`, and
 * then every control inside it counts too. On /signup at 390×844 the corner
 * covered the marketing opt-in checkbox at first paint (P13, 2026-10-10) and
 * mid-screen right clipped the Email field, so when both spots cover
 * something the trigger tucks into a 28px tab on the left edge instead.
 */

import { useLayoutEffect, useState } from 'react';

/** The cookie banner's marker (a page's primary action), plus every control of a marked form. */
const AVOID_SELECTOR = [
  '[data-cookie-avoid]',
  ...['input:not([type="hidden"])', 'select', 'textarea', 'button', 'a[href]'].map((c) => `[data-float-avoid] ${c}`),
].join(', ');

export type TriggerPlacement = 'corner' | 'side' | 'edge';

export const TRIGGER_POSITION: Record<TriggerPlacement, string> = {
  corner: 'bottom-20 left-5 w-12 rounded-full',
  side: 'top-1/2 right-2 -translate-y-1/2 w-12 rounded-full',
  edge: 'bottom-20 left-0 w-7 rounded-r-full',
};

interface Box { left: number; right: number; top: number; bottom: number }

/** Each placement's footprint in px: bottom-20 (80), left-5 (20), right-2 (8), w-12/h-12 (48), w-7 (28). */
function footprint(placement: TriggerPlacement, width: number, height: number): Box {
  if (placement === 'side') return { left: width - 56, right: width - 8, top: height / 2 - 24, bottom: height / 2 + 24 };
  const top = height - 128;
  const bottom = height - 80;
  return placement === 'corner' ? { left: 20, right: 68, top, bottom } : { left: 0, right: 28, top, bottom };
}

function avoidedBoxes(): Box[] {
  return Array.from(document.querySelectorAll(AVOID_SELECTOR))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
}

function covers(a: Box, boxes: Box[]): boolean {
  return boxes.some((r) => r.left < a.right && r.right > a.left && r.top < a.bottom && r.bottom > a.top);
}

/** The first spot that covers nothing avoided; the edge tab, the smallest footprint, when none is clear. */
export function choosePlacement(width: number, height: number, boxes: Box[]): TriggerPlacement {
  return (['corner', 'side'] as const).find((p) => !covers(footprint(p, width, height), boxes)) ?? 'edge';
}

export function useTriggerPlacement(): TriggerPlacement {
  const [placement, setPlacement] = useState<TriggerPlacement>('corner');

  useLayoutEffect(() => {
    const place = () => setPlacement(choosePlacement(window.innerWidth, window.innerHeight, avoidedBoxes()));
    // The widget mounts once, outside the router; the lazy landing chunk adds
    // (and a navigation removes) the marked CTA later, so re-check when the
    // DOM changes too — at most once a frame.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };
    const observer = new MutationObserver(schedule);
    place();
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { passive: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place);
    };
  }, []);

  return placement;
}
